import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { formatClientCode } from '@/lib/clientCode';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, User, Building2, Phone, Mail, MapPin, Globe, Briefcase, DollarSign, TrendingUp, CreditCard, Download, IdCard, FileImage, PenTool, UserSquare2, TrendingDown, ZoomIn, X, Landmark, History } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { KycFormSection } from '@/components/KycFormSection';

export default function ClientProfilePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/profile`);
        setProfile(data);
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    fetchProfile();
  }, [clientId]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;
  if (!profile) return <div className="text-center py-12 text-brand-text-secondary">Client not found</div>;

  const { client, branch, trader, manager, balance, accounts, kyc, recent_transactions = [], recent_pnl = [], recent_futures = [] } = profile;

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={18} /></Button>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">{client?.name}</h1>
          <p className="text-sm text-brand-text-secondary mt-0.5">
            <span className="font-mono text-brand-primary font-semibold">{formatClientCode(client)}</span>
            {client?.email && <span className="ml-3">{client.email}</span>}
          </p>
        </div>
        <Badge className={`ml-auto text-xs border-0 ${client?.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{client?.status}</Badge>
        <Button variant="outline" size="sm" className="rounded-lg text-xs ml-2" data-testid="view-trades-btn" onClick={() => setActiveTab('trades')}>
          <History size={14} className="mr-1" /> Trade History
        </Button>
        <Button variant="outline" size="sm" className="rounded-lg text-xs" data-testid="download-statement-btn" onClick={async () => {
          try { const res = await api.get(`/exports/client-statement/${clientId}`, { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `statement-${client?.client_id}.pdf`; a.click(); URL.revokeObjectURL(url); toast.success('Statement downloaded'); } catch { toast.error('Download failed'); }
        }}><Download size={14} className="mr-1" /> Statement</Button>
      </div>

      {/* Balance Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center"><DollarSign size={18} className="text-brand-primary" /></div>
            <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Main Balance</p><p className="font-heading text-xl text-brand-text-primary" data-testid="admin-main-balance">${(balance?.main_balance ?? balance?.current_balance ?? 0)?.toLocaleString() || '0'}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-brand-success/10 flex items-center justify-center"><TrendingUp size={18} className="text-brand-success" /></div>
            <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Net P&L</p><p className={`font-heading text-xl ${(balance?.net_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${balance?.net_pnl?.toLocaleString() || '0'}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-brand-accent/10 flex items-center justify-center"><DollarSign size={18} className="text-brand-accent" /></div>
            <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">All-Time Profit</p><p className="font-heading text-xl text-brand-text-primary">${(balance?.total_pnl > 0 ? balance.total_pnl : 0)?.toLocaleString() || '0'}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center"><CreditCard size={18} className="text-brand-primary" /></div>
            <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Withdrawable</p><p className="font-heading text-xl text-brand-primary">${(balance?.main_balance ?? balance?.current_balance ?? 0)?.toLocaleString() || '0'}</p></div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-brand-bg/50 border border-brand-border/50 rounded-lg p-0.5">
          {[{ v: 'overview', l: 'Overview' }, { v: 'file360', l: 'Client 360 File' }, { v: 'trades', l: 'Trade History' }, { v: 'kyc', l: 'KYC' }].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-md text-xs px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-brand-primary" data-testid={`tab-${t.v}`}>{t.l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Details */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium text-brand-text-primary flex items-center gap-2"><User size={18} className="text-brand-primary" /> Client Details</CardTitle></CardHeader>
          <CardContent className="p-6 pt-2 space-y-3">
            <InfoRow label="Client ID" value={formatClientCode(client)} mono />
            <InfoRow label="Name" value={client?.name} />
            <InfoRow label="Email" value={client?.email} />
            <InfoRow label="Phone" value={client?.phone} />
            <InfoRow label="KYC Status" value={client?.kyc_status} badge />
            <InfoRow label="Created" value={client?.created_at ? new Date(client.created_at).toLocaleDateString() : '-'} />
          </CardContent>
        </Card>

        {/* Support / Contact Section */}
        <Card className="border border-brand-border bg-white" data-testid="client-support-section">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium text-brand-text-primary flex items-center gap-2"><Phone size={18} className="text-brand-primary" /> Support & Contact</CardTitle></CardHeader>
          <CardContent className="p-6 pt-2 space-y-4">
            {branch && (
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><Building2 size={13} /> Branch Office</p>
                <p className="text-sm font-medium text-brand-text-primary">{branch.name}{branch.city ? `, ${branch.city}` : ''}</p>
                {branch.address && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><MapPin size={12} /> {branch.address}</p>}
                {branch.phone && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Phone size={12} /> {branch.phone}</p>}
                {branch.email && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Mail size={12} /> {branch.email}</p>}
              </div>
            )}
            {manager && (
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><Briefcase size={13} /> Branch Manager</p>
                <p className="text-sm font-medium text-brand-text-primary">{manager.name}</p>
                {manager.phone && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Phone size={12} /> {manager.phone}</p>}
                {manager.email && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Mail size={12} /> {manager.email}</p>}
              </div>
            )}
            {trader && (
              <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><User size={13} /> Assigned Trader</p>
                <p className="text-sm font-medium text-brand-text-primary">{trader.name}</p>
                {trader.phone && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Phone size={12} /> {trader.phone}</p>}
              </div>
            )}
            <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-1">
              <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><Globe size={13} /> Company</p>
              <p className="text-sm font-medium text-brand-text-primary">QUANTORA ANALYTICS PRIVATE LIMITED</p>
              <p className="text-xs text-brand-text-secondary">quantora.co.in</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trading Accounts */}
      {accounts?.length > 0 && (
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium text-brand-text-primary">Trading Accounts</CardTitle></CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-brand-border">{['Broker', 'Trading ID', 'Type', 'Balance', 'Status'].map(h => <th key={h} className="text-left py-2 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
                <tbody>{accounts.map(a => (
                  <tr key={a.id} className="border-b border-brand-border/50">
                    <td className="py-2 px-3 text-brand-text-primary">{a.broker_name}</td>
                    <td className="py-2 px-3 font-mono text-xs">{a.trading_id}</td>
                    <td className="py-2 px-3 capitalize">{a.account_type}</td>
                    <td className="py-2 px-3 font-medium">${Number(a.balance || 0).toLocaleString()}</td>
                    <td className="py-2 px-3"><Badge className={`text-xs border-0 ${a.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{a.status}</Badge></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>
        <TabsContent value="file360" className="space-y-4">
          <ClientFile360 client={client} kyc={kyc} balance={balance} accounts={accounts}
            recentTx={recent_transactions} recentPnl={recent_pnl} recentFutures={recent_futures} />
        </TabsContent>
        <TabsContent value="trades" className="space-y-4">
          <FullTradeHistory clientId={clientId} kyc={kyc} />
        </TabsContent>
        <TabsContent value="kyc">
          <KycFormSection clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const InfoRow = ({ label, value, mono, badge }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-brand-border/30 last:border-0">
    <span className="text-xs text-brand-text-secondary">{label}</span>
    {badge ? (
      <Badge className={`text-xs border-0 ${value === 'verified' ? 'bg-brand-success/15 text-brand-success' : value === 'rejected' ? 'bg-brand-error/15 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>{value || '-'}</Badge>
    ) : (
      <span className={`text-sm ${mono ? 'font-mono text-brand-primary font-semibold' : 'text-brand-text-primary'}`}>{value || '-'}</span>
    )}
  </div>
);

// ─── Banking-style "Client 360 File" ───
function ClientFile360({ client, kyc, balance, accounts = [], recentTx = [], recentPnl = [], recentFutures = [] }) {
  const kycVerified = client?.kyc_status === 'verified';
  const [zoomDoc, setZoomDoc] = useState(null); // { label, src }
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const shortDate = (s) => s ? new Date(s).toLocaleDateString() : '-';
  const fullDate = (s) => s ? new Date(s).toLocaleString() : '-';

  const openDoc = (label, src) => { if (src) setZoomDoc({ label, src }); };
  const downloadDoc = async (label, src) => {
    if (!src) return;
    try {
      const resp = await fetch(src);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
      a.download = `${(client?.client_id || 'client')}-${label.toLowerCase().replace(/\s+/g, '-')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-4" data-testid="client-file-360">
      {/* Banking-style header — Photo + Signature + core IDs */}
      <Card className="border border-brand-border bg-gradient-to-br from-white to-brand-bg/40">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-5">
            {/* Photo */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => openDoc('Photo', kyc?.photo_doc)}
                disabled={!kyc?.photo_doc}
                className="w-28 h-32 border-2 border-brand-border rounded-lg overflow-hidden bg-brand-bg flex items-center justify-center relative group disabled:cursor-default enabled:hover:border-brand-primary transition-colors"
                data-testid="file360-photo"
              >
                {kyc?.photo_doc ? (
                  <>
                    <img src={kyc.photo_doc} alt="Client Photo" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ZoomIn size={22} className="text-white" />
                    </div>
                  </>
                ) : (
                  <UserSquare2 size={40} className="text-brand-text-secondary/40" />
                )}
              </button>
              <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Photo</p>
            </div>
            {/* Central details */}
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-2xl font-bold text-brand-text-primary">{kyc?.full_name || client?.name || '-'}</h3>
                <Badge className={`text-[10px] border-0 ${kycVerified ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>
                  KYC {client?.kyc_status || 'pending'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-brand-text-secondary">Account No.</span> <span className="font-mono font-bold text-brand-primary" data-testid="file360-client-id">{formatClientCode(client)}</span></div>
                <div><span className="text-brand-text-secondary">DOB</span> <span className="text-brand-text-primary">{shortDate(kyc?.date_of_birth)}</span></div>
                <div><span className="text-brand-text-secondary">Phone</span> <span className="text-brand-text-primary">{kyc?.phone || client?.phone || '-'}</span></div>
                <div><span className="text-brand-text-secondary">Email</span> <span className="text-brand-text-primary break-all">{kyc?.email || client?.email || '-'}</span></div>
                <div><span className="text-brand-text-secondary">PAN</span> <span className="font-mono text-brand-text-primary">{kyc?.pan_number || '-'}</span></div>
                <div><span className="text-brand-text-secondary">Aadhaar</span> <span className="font-mono text-brand-text-primary">{kyc?.aadhaar_number ? `xxxx-xxxx-${String(kyc.aadhaar_number).slice(-4)}` : '-'}</span></div>
              </div>
            </div>
            {/* Signature */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => openDoc('Signature', kyc?.signature_doc)}
                disabled={!kyc?.signature_doc}
                className="w-32 h-16 border-2 border-brand-border rounded-lg overflow-hidden bg-white flex items-center justify-center relative group disabled:cursor-default enabled:hover:border-brand-primary transition-colors"
                data-testid="file360-signature"
              >
                {kyc?.signature_doc ? (
                  <>
                    <img src={kyc.signature_doc} alt="Signature" className="w-full h-full object-contain" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ZoomIn size={18} className="text-white" />
                    </div>
                  </>
                ) : (
                  <PenTool size={22} className="text-brand-text-secondary/40" />
                )}
              </button>
              <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Signature</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balances strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatMini icon={<DollarSign size={14} />} label="Main Balance" value={money(balance?.main_balance ?? balance?.current_balance)} />
        <StatMini icon={<CreditCard size={14} />} label="Total Investment" value={money(balance?.active_investment ?? balance?.total_investment)} />
        <StatMini icon={<TrendingUp size={14} />} label="Net P&L" value={money(balance?.net_pnl)} tone={(balance?.net_pnl || 0) >= 0 ? 'ok' : 'bad'} />
        <StatMini icon={<TrendingDown size={14} />} label="Withdrawn" value={money(balance?.total_withdrawn)} />
      </div>

      {/* Document images */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><IdCard size={14} className="text-brand-primary" /> KYC Documents <span className="text-[10px] font-normal text-brand-text-secondary ml-2">Click any doc to zoom</span></CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <DocPreview label="PAN Card" src={kyc?.pan_card_doc} testid="file360-pan" onClick={openDoc} />
          <DocPreview label="Aadhaar Card" src={kyc?.aadhaar_doc} testid="file360-aadhaar" onClick={openDoc} />
          <DocPreview label="Photo" src={kyc?.photo_doc} testid="file360-photo-full" onClick={openDoc} />
          <DocPreview label="Signature" src={kyc?.signature_doc} testid="file360-signature-full" onClick={openDoc} />
        </CardContent>
      </Card>

      {/* Address + Nominee + Financial */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><MapPin size={14} className="text-brand-primary" /> Address</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 space-y-1">
            <InfoRow label="Address" value={kyc?.address} />
            <InfoRow label="City" value={kyc?.city} />
            <InfoRow label="State" value={kyc?.state} />
            <InfoRow label="PIN" value={kyc?.pin_code} mono />
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><User size={14} className="text-brand-primary" /> Nominee</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 space-y-1">
            <InfoRow label="Name" value={kyc?.nominee_name} />
            <InfoRow label="DOB" value={shortDate(kyc?.nominee_dob)} />
            <InfoRow label="Relationship" value={kyc?.nominee_relationship} />
            <InfoRow label="Father Name" value={kyc?.father_name} />
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Briefcase size={14} className="text-brand-primary" /> Financial</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 space-y-1">
            <InfoRow label="Income Source" value={kyc?.income_source} />
            <InfoRow label="Occupation" value={kyc?.occupation} />
            <InfoRow label="Mother Name" value={kyc?.mother_name} />
          </CardContent>
        </Card>
      </div>

      {/* Bank & UPI for payouts */}
      <Card className="border border-brand-border bg-white" data-testid="file360-bank-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Landmark size={14} className="text-brand-primary" /> Bank & UPI (Payout Details)</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
          <InfoRow label="Account No." value={kyc?.bank_account_number ? `xxxx-${String(kyc.bank_account_number).slice(-4)}` : '-'} mono />
          <InfoRow label="IFSC" value={kyc?.bank_ifsc} mono />
          <InfoRow label="Holder" value={kyc?.bank_holder_name} />
          <InfoRow label="Bank" value={kyc?.bank_name} />
          <InfoRow label="Branch" value={kyc?.bank_branch} />
          <InfoRow label="UPI ID" value={kyc?.upi_id} mono />
        </CardContent>
      </Card>

      {/* Trading Accounts */}
      {accounts?.length > 0 && (
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Trading Accounts</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 overflow-x-auto">
            <table className="w-full text-xs" data-testid="file360-accounts">
              <thead><tr className="border-b border-brand-border">{['Broker', 'Trading ID', 'Type', 'Balance', 'Status'].map(h => <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
              <tbody>{accounts.map(a => (
                <tr key={a.id} className="border-b border-brand-border/50">
                  <td className="py-2 px-3">{a.broker_name}</td>
                  <td className="py-2 px-3 font-mono">{a.trading_id}</td>
                  <td className="py-2 px-3 capitalize">{a.account_type}</td>
                  <td className="py-2 px-3 font-medium">${Number(a.balance || 0).toLocaleString()}</td>
                  <td className="py-2 px-3"><Badge className={`text-[10px] border-0 ${a.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{a.status}</Badge></td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions + Trading History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Transactions</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 overflow-x-auto">
            {recentTx.length === 0 ? <p className="text-xs text-brand-text-secondary py-4 text-center">No transactions</p> : (
              <table className="w-full text-xs" data-testid="file360-transactions">
                <thead><tr className="border-b border-brand-border">{['Type', 'Amount', 'Status', 'Date'].map(h => <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
                <tbody>{recentTx.map(t => (
                  <tr key={t.id} className="border-b border-brand-border/50">
                    <td className="py-1.5 px-2 capitalize">{t.type}</td>
                    <td className={`py-1.5 px-2 font-mono font-semibold ${t.type === 'deposit' ? 'text-brand-success' : 'text-brand-error'}`}>{money(t.amount)}</td>
                    <td className="py-1.5 px-2"><Badge className={`text-[9px] border-0 ${t.status === 'approved' ? 'bg-brand-success/15 text-brand-success' : t.status === 'rejected' ? 'bg-brand-error/15 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>{t.status}</Badge></td>
                    <td className="py-1.5 px-2 text-brand-text-secondary">{shortDate(t.created_at)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Trading History</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 overflow-x-auto">
            {recentPnl.length === 0 && recentFutures.length === 0 ? <p className="text-xs text-brand-text-secondary py-4 text-center">No trades</p> : (
              <table className="w-full text-xs" data-testid="file360-trades">
                <thead><tr className="border-b border-brand-border">{['Symbol', 'Type', 'Net P&L', 'Date'].map(h => <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
                <tbody>
                  {recentPnl.map(p => (
                    <tr key={`pnl-${p.id}`} className="border-b border-brand-border/50">
                      <td className="py-1.5 px-2 font-medium">{p.symbol || '-'}</td>
                      <td className="py-1.5 px-2"><Badge className="text-[9px] border-0 bg-brand-primary/10 text-brand-primary">Spot</Badge></td>
                      <td className={`py-1.5 px-2 font-mono font-semibold ${(p.net_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{money(p.net_pnl)}</td>
                      <td className="py-1.5 px-2 text-brand-text-secondary">{p.date || shortDate(p.created_at)}</td>
                    </tr>
                  ))}
                  {recentFutures.map(f => (
                    <tr key={`fut-${f.id}`} className="border-b border-brand-border/50">
                      <td className="py-1.5 px-2 font-medium">{f.symbol || '-'}</td>
                      <td className="py-1.5 px-2"><Badge className={`text-[9px] border-0 ${f.side === 'long' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>Fut {f.side}</Badge></td>
                      <td className={`py-1.5 px-2 font-mono font-semibold ${(f.realized_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{money(f.realized_pnl)}</td>
                      <td className="py-1.5 px-2 text-brand-text-secondary">{shortDate(f.closed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Zoom / doc-view dialog */}
      <Dialog open={!!zoomDoc} onOpenChange={(o) => { if (!o) setZoomDoc(null); }}>
        <DialogContent className="max-w-4xl bg-white" data-testid="doc-zoom-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4 pr-8">
              <span className="flex items-center gap-2 text-base">
                <IdCard size={16} className="text-brand-primary" /> {zoomDoc?.label}
              </span>
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] border-0 ${kyc?.status === 'verified' ? 'bg-brand-success/15 text-brand-success' : kyc?.status === 'rejected' ? 'bg-brand-error/15 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>
                  KYC: {kyc?.status || 'not submitted'}
                </Badge>
                <Button size="sm" variant="outline" onClick={() => downloadDoc(zoomDoc?.label, zoomDoc?.src)} className="rounded-lg text-xs" data-testid="doc-zoom-download">
                  <Download size={12} className="mr-1" /> Download
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
            <div className="md:col-span-3 bg-brand-bg/60 rounded-lg p-3 flex items-center justify-center min-h-[420px]">
              {zoomDoc?.src && <img src={zoomDoc.src} alt={zoomDoc.label} className="max-w-full max-h-[70vh] object-contain rounded" data-testid="doc-zoom-image" />}
            </div>
            <div className="space-y-2 text-xs">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Document metadata</p>
              <div className="bg-brand-bg/50 rounded-lg p-3 space-y-1.5 border border-brand-border/50">
                <div className="flex justify-between"><span className="text-brand-text-secondary">Client</span><span className="font-medium text-brand-text-primary">{client?.name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-brand-text-secondary">Account No.</span><span className="font-mono text-brand-primary">{formatClientCode(client)}</span></div>
                <div className="flex justify-between"><span className="text-brand-text-secondary">Uploaded</span><span className="text-brand-text-primary">{fullDate(kyc?.submitted_at)}</span></div>
                {kyc?.reviewed_at && <div className="flex justify-between"><span className="text-brand-text-secondary">Reviewed</span><span className="text-brand-text-primary">{fullDate(kyc?.reviewed_at)}</span></div>}
                {kyc?.reviewed_by_name && <div className="flex justify-between"><span className="text-brand-text-secondary">Reviewer</span><span className="text-brand-text-primary">{kyc?.reviewed_by_name}</span></div>}
                {kyc?.review_notes && (
                  <div className="pt-1.5 border-t border-brand-border/40">
                    <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary mb-1">Notes</p>
                    <p className="text-brand-text-primary italic">{kyc.review_notes}</p>
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setZoomDoc(null)} className="w-full text-xs" data-testid="doc-zoom-close"><X size={12} className="mr-1" /> Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatMini = ({ icon, label, value, tone }) => (
  <div className="bg-white border border-brand-border rounded-lg p-3 flex items-center gap-2.5">
    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tone === 'ok' ? 'bg-brand-success/10 text-brand-success' : tone === 'bad' ? 'bg-brand-error/10 text-brand-error' : 'bg-brand-primary/10 text-brand-primary'}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-widest font-semibold text-brand-text-secondary truncate">{label}</p>
      <p className={`font-mono font-bold text-sm ${tone === 'ok' ? 'text-brand-success' : tone === 'bad' ? 'text-brand-error' : 'text-brand-text-primary'}`}>{value}</p>
    </div>
  </div>
);

const DocPreview = ({ label, src, testid, onClick }) => (
  <div className="text-center">
    <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">{label}</p>
    <button
      type="button"
      onClick={() => onClick?.(label, src)}
      disabled={!src}
      className="w-full h-28 bg-brand-bg rounded-lg border border-brand-border enabled:hover:border-brand-primary flex items-center justify-center overflow-hidden relative group disabled:cursor-default transition-colors"
      data-testid={testid}
    >
      {src ? (
        <>
          <img src={src} alt={label} className="w-full h-full object-contain" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <ZoomIn size={22} className="text-white" />
          </div>
        </>
      ) : (
        <div className="text-brand-text-secondary/50 flex flex-col items-center gap-1">
          <FileImage size={20} />
          <span className="text-[9px]">Not uploaded</span>
        </div>
      )}
    </button>
  </div>
);


// ─── Full Trade History (Spot P&L + Futures) ───
function FullTradeHistory({ clientId, kyc }) {
  const [data, setData] = useState({ spot: [], futures: [], counts: { spot: 0, futures: 0 } });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    api.get(`/clients/${clientId}/trades?limit=1000`).then(res => setData(res.data))
      .catch(err => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [clientId]);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const shortDate = (s) => s ? new Date(s).toLocaleString() : '-';

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;

  const spotRows = data.spot || [];
  const futuresRows = data.futures || [];
  const spotNet = spotRows.reduce((s, p) => s + (p.net_pnl || 0), 0);
  const futuresNet = futuresRows.reduce((s, p) => s + (p.realized_pnl || 0), 0);

  return (
    <div className="space-y-4" data-testid="client-full-trades">
      {/* Summary + Bank/UPI recap */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-brand-border rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Spot Trades</p>
          <p className="font-heading text-xl text-brand-text-primary mt-1">{data.counts?.spot || 0}</p>
          <p className={`text-xs font-mono font-semibold mt-1 ${spotNet >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>Net {money(spotNet)}</p>
        </div>
        <div className="bg-white border border-brand-border rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Futures Trades</p>
          <p className="font-heading text-xl text-brand-text-primary mt-1">{data.counts?.futures || 0}</p>
          <p className={`text-xs font-mono font-semibold mt-1 ${futuresNet >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>Net {money(futuresNet)}</p>
        </div>
        <div className="bg-gradient-to-br from-brand-primary/5 to-white border border-brand-primary/20 rounded-lg p-4 flex flex-col justify-center">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Payout destination</p>
          <p className="text-sm font-semibold text-brand-text-primary mt-1 truncate">
            {kyc?.bank_holder_name || kyc?.full_name || '—'}
          </p>
          <p className="text-xs text-brand-text-secondary font-mono">
            {kyc?.bank_account_number ? `A/c xxxx-${String(kyc.bank_account_number).slice(-4)} · ${kyc?.bank_ifsc || ''}` : (kyc?.upi_id ? `UPI: ${kyc.upi_id}` : 'No payout details')}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[{ v: 'all', l: `All (${(data.counts?.spot || 0) + (data.counts?.futures || 0)})` },
          { v: 'spot', l: `Spot (${data.counts?.spot || 0})` },
          { v: 'futures', l: `Futures (${data.counts?.futures || 0})` }].map(t => (
          <Button key={t.v} size="sm" variant={tab === t.v ? 'default' : 'outline'} onClick={() => setTab(t.v)}
            className={`rounded-lg text-xs ${tab === t.v ? 'bg-brand-primary text-white' : 'border-brand-border'}`}
            data-testid={`trade-filter-${t.v}`}>{t.l}</Button>
        ))}
      </div>

      {/* Combined table */}
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="full-trades-table">
            <thead><tr className="border-b border-brand-border bg-brand-bg/50">
              {['Type', 'Symbol', 'Side', 'Entry', 'Exit', 'Qty', 'Net P&L', 'Date'].map(h => (
                <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(tab === 'all' || tab === 'spot') && spotRows.map(p => (
                <tr key={`s-${p.id}`} className="border-b border-brand-border/50 hover:bg-brand-surface-hover">
                  <td className="py-2 px-3"><Badge className="text-[9px] border-0 bg-brand-primary/10 text-brand-primary">Spot</Badge></td>
                  <td className="py-2 px-3 font-medium">{p.symbol || '-'}</td>
                  <td className="py-2 px-3"><Badge className={`text-[9px] border-0 ${p.direction === 'long' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{p.direction?.toUpperCase() || '-'}</Badge></td>
                  <td className="py-2 px-3 font-mono">${p.entry_price?.toFixed(2) || '-'}</td>
                  <td className="py-2 px-3 font-mono">${p.exit_price?.toFixed(2) || '-'}</td>
                  <td className="py-2 px-3 font-mono">{p.quantity ?? '-'}</td>
                  <td className={`py-2 px-3 font-mono font-semibold ${(p.net_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{money(p.net_pnl)}</td>
                  <td className="py-2 px-3 text-brand-text-secondary">{p.date || shortDate(p.created_at)}</td>
                </tr>
              ))}
              {(tab === 'all' || tab === 'futures') && futuresRows.map(p => (
                <tr key={`f-${p.id}`} className="border-b border-brand-border/50 hover:bg-brand-surface-hover">
                  <td className="py-2 px-3"><Badge className="text-[9px] border-0 bg-amber-100 text-amber-700">Futures {p.leverage}x</Badge></td>
                  <td className="py-2 px-3 font-medium">{p.symbol || '-'}</td>
                  <td className="py-2 px-3"><Badge className={`text-[9px] border-0 ${p.side === 'long' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{p.side?.toUpperCase() || '-'}</Badge></td>
                  <td className="py-2 px-3 font-mono">${p.entry_price?.toFixed(2) || '-'}</td>
                  <td className="py-2 px-3 font-mono">${p.exit_price?.toFixed(2) || '-'}</td>
                  <td className="py-2 px-3 font-mono">{p.quantity?.toFixed(4) || '-'}</td>
                  <td className={`py-2 px-3 font-mono font-semibold ${(p.realized_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{money(p.realized_pnl)}</td>
                  <td className="py-2 px-3 text-brand-text-secondary">{shortDate(p.closed_at)}</td>
                </tr>
              ))}
              {((tab === 'all' && !spotRows.length && !futuresRows.length) ||
                (tab === 'spot' && !spotRows.length) ||
                (tab === 'futures' && !futuresRows.length)) && (
                <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No trades yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
